// Sidebar component as specified in appmark.txt
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  HomeIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon,
  WorkflowIcon,
  List,
  Globe,
  Key,
  BookOpen,
  UsersIcon,
  UserIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useUIStore, useAuthStore } from "../store";
import { Permission, hasPermission } from "../utils/permissions";

const baseNavigation = [
  { name: "Dashboard", href: "/", icon: HomeIcon, permission: null as string | null },
  { name: "Pipelines", href: "/pipelines", icon: List, permission: null },
  { name: "Playbooks", href: "/playbooks", icon: BookOpen, permission: null },
  { name: "A2A Agents", href: "/a2a-agents", icon: Globe, permission: null },
  { name: "API Keys", href: "/api-keys", icon: Key, permission: Permission.API_KEY_CREATE },
  { name: "Groups", href: "/groups", icon: UsersIcon, permission: Permission.GROUP_CREATE },
  { name: "Users", href: "/users", icon: UserIcon, permission: Permission.USER_ASSIGN_ROLE },
  { name: "Roles", href: "/roles", icon: ShieldCheckIcon, permission: Permission.ROLE_CREATE },
  { name: "System Health", href: "/health", icon: HeartIcon, permission: Permission.ADMIN },
  { name: "Settings", href: "/settings", icon: CogIcon, permission: null },
];

export const Sidebar: React.FC = () => {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { user } = useAuthStore();
  const location = useLocation();

  // Filter nav items based on RBAC permissions
  const visibleNavigation = baseNavigation.filter(
    (item) => !item.permission || hasPermission(user, item.permission)
  );

  // Show Workflows link only when viewing a specific workflow
  const showWorkflows = location.pathname.startsWith("/workflows/");

  const navigation = showWorkflows
    ? [
        visibleNavigation[0], // Dashboard
        { name: "Workflows", href: "/workflows", icon: WorkflowIcon, permission: null },
        ...visibleNavigation.slice(1), // Rest of navigation
      ]
    : visibleNavigation;

  return (
    <div
      className={`fixed inset-y-0 left-0 z-40 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        sidebarCollapsed ? "w-16" : "w-72"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        {!sidebarCollapsed && (
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            RoboWorks
          </h1>
        )}
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          data-testid="sidebar-toggle"
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="h-5 w-5" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-8 px-3">
        <ul className="space-y-2">
          {navigation.map((item) => (
            <li key={item.name} className="relative">
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                  }`
                }
              >
                <item.icon
                  className={`flex-shrink-0 h-5 w-5 ${
                    sidebarCollapsed ? "mx-auto" : "mr-3"
                  }`}
                />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
